// =============================================================================
// postAuthGrace.js — Post-auth deep link + first-request 401 grace window
// =============================================================================
//
// What this module solves
// -----------------------
// Two related auth/UX problems share this module because they share state:
//
// 1) DEEP LINK HISTORY
//    A logged-out user pastes a URL like
//      `https://portal/#/orders/sales?CompanyRefNumber=GATES10`
//    MSAL redirects them to Microsoft, the redirect strips the original hash,
//    and they land back on `#/`. Without help, they would end up on `#/dashboard`
//    instead of the page they actually wanted.
//    Solution: stash the original hash in sessionStorage BEFORE the MSAL
//    redirect (see `src/index.js`), and read it back AFTER auth finalizes
//    (see `src/helpers/auth-finalize.js`) to restore the deep link.
//
// 2) POST-AUTH 401 GRACE
//    The user might restore a deep link to a page their account does not have
//    permission to load. The first protected API call from that page returns
//    401, and the axios interceptor (`src/helpers/client.js`) would normally
//    log them out — a poor UX for "wrong page, right account".
//    Solution: on the post-auth redirect, ARM a one-shot grace window. If
//    that very first 401 comes in while the user is still on the landing
//    hash, redirect them to a safe fallback (`#/dashboard` or `#/holding`)
//    instead of nuking the session. The window self-disarms after one use,
//    and also disarms as soon as the user navigates anywhere else.
//
// IMPORTANT: this module is NOT a token-expiry bouncer. The 401 interceptor
// runs the silent-refresh path FIRST for expired/invalid bearer tokens; the
// grace check only runs for 401s that fall through that path (no bearer,
// non-idempotent method, or "valid token but forbidden"). Genuine session
// expiry still results in a logout.
//
// Lifecycle at a glance
// ---------------------
//   ARM   → on a successful auth redirect (auth-finalize.js)
//   CONSUME → on the first matching 401 (client.js axios interceptor)
//   CLEAR → on hashchange away from landing (app.js), or on logout
//           (user.actions.js → clearAuthRelatedSessionStorage)
//
// Storage scope
// -------------
// Everything lives in `sessionStorage`, so it evaporates when the tab closes.
// Every read/write is wrapped in try/catch — Safari ITP, private browsing,
// and locked-down corporate browsers can throw on storage access, and the
// behavior here is best-effort UX (worst case: deep link is lost, user ends
// up on the dashboard instead of the saved page — never unsafe).
// =============================================================================

// -----------------------------------------------------------------------------
// Storage keys
// -----------------------------------------------------------------------------
//
// Two logically separate groups of keys live in this module:
//
//   "deep-link history pair"
//     `history`                    — the saved pre-auth hash to restore
//     `historyMsalHomeAccountId`   — which MSAL account stashed it (anti-mixup)
//
//   "post-auth grace triple"
//     `ads.auth.postAuthLandingHash`   — where the redirect took the user
//     `ads.auth.postAuthFallbackRoute` — where to bounce on first 401
//     `ads.auth.postAuthGraceActive`   — sentinel "1" while window is armed

/**
 * sessionStorage key under which we save the user's pre-auth hash so we can
 * restore the deep link after the MSAL redirect strips the URL.
 *
 * Read in `auth-finalize.js`, written in `index.js` (msalReady block).
 * Cleared by `clearDeepLinkHistoryPair` and on logout.
 *
 * Exported because `index.js` and a couple of tests need to address it
 * symbolically rather than via a string literal.
 */
export const DEEP_LINK_HISTORY_HASH_KEY = "history";

/**
 * sessionStorage key holding the MSAL `homeAccountId` that was active when
 * the deep-link history was saved.
 *
 * Why we need this:
 *   User A pastes a deep link in tab 1, gets redirected to log in, but then
 *   logs in as User B on the same browser session. Without this guard, User
 *   B would be redirected to User A's saved hash. By comparing the stored
 *   `homeAccountId` against the freshly authenticated account's id, we can
 *   detect "wrong user" and discard the stale deep link.
 *
 * Used by `isDeepLinkHistoryStaleForMsalAccount` below.
 */
export const DEEP_LINK_HISTORY_MSAL_HOME_ACCOUNT_KEY =
  "historyMsalHomeAccountId";

/**
 * Where the redirect took the user. While the grace window is armed, this is
 * the only hash on which a 401 may be absorbed into a fallback redirect.
 *
 * Format: a normalized hash starting with `#` (e.g. `#/orders/sales?id=1`).
 */
const POST_AUTH_LANDING_HASH_KEY = "ads.auth.postAuthLandingHash";

/**
 * Safe route to bounce to if the user 401s on the landing hash. Computed
 * by `auth-finalize.js` from the user profile:
 *   - `#/dashboard` if they have permissions + a team, otherwise
 *   - `#/holding`
 *
 * For the holding-page case, landing == fallback, which the consume helper
 * deliberately treats as "no bounce" to avoid redirect loops.
 */
const POST_AUTH_FALLBACK_ROUTE_KEY = "ads.auth.postAuthFallbackRoute";

/**
 * Sentinel: when set to `"1"` the grace window is armed. We use a sentinel
 * key (rather than presence of landing/fallback) so we can deliberately
 * "disarm without clearing" if we ever need to (currently we always clear,
 * but the explicit flag keeps the contract clear).
 */
const POST_AUTH_GRACE_ACTIVE_KEY = "ads.auth.postAuthGraceActive";

// -----------------------------------------------------------------------------
// Hash normalization
// -----------------------------------------------------------------------------

/**
 * Normalizes any incoming hash-ish string for storage and comparison.
 *
 * Why this exists:
 *   `landingHash` and `currentHash` come from many places (window.location.hash,
 *   stored values, redirects we computed ourselves) and not all of them
 *   reliably include the leading `#`. We persist and compare the canonical
 *   `#/...` form so equality checks (cur === landing) are robust.
 *
 *   Empty / whitespace / null / undefined → "" (the "no hash" sentinel).
 *
 * @param {unknown} value any string-coercible value
 * @returns {string} `""` for nullish/empty input, otherwise a `#`-prefixed hash
 */
export function normalizeHashForGrace(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  return s.startsWith("#") ? s : `#${s}`;
}

// -----------------------------------------------------------------------------
// Storage clearers
// -----------------------------------------------------------------------------

/**
 * Removes the deep-link history pair (saved hash + which MSAL account saved it).
 *
 * Called whenever the saved deep link should no longer be acted on:
 *   - After a successful redirect to it (one-shot semantics in auth-finalize)
 *   - When the saved id belongs to a different user than the one logging in
 *   - On logout (via `clearAuthRelatedSessionStorage`)
 *   - Before navigating to certain "safe" routes that should not be overridden
 *     by a stale history (Full.js POP handler, exchanges/sales/inventory)
 *
 * Errors are swallowed because storage access can throw under Safari ITP /
 * private browsing — losing the entry is benign here, propagating an
 * exception would break the auth flow.
 */
export function clearDeepLinkHistoryPair() {
  try {
    sessionStorage.removeItem(DEEP_LINK_HISTORY_HASH_KEY);
    sessionStorage.removeItem(DEEP_LINK_HISTORY_MSAL_HOME_ACCOUNT_KEY);
  } catch (_e) {
    // Ignore: storage may be unavailable (private mode, ITP, MDM lockdown).
  }
}

/**
 * Removes the post-auth grace triple (landing hash, fallback route, active flag).
 *
 * Called when the grace window should be disarmed:
 *   - Internally by `consumePostAuth401Fallback` after one absorption (one-shot)
 *   - By `maybeClearPostAuthGraceOnHashChange` when the user navigates away
 *   - On logout (via `clearAuthRelatedSessionStorage`)
 */
export function clearPostAuthGrace() {
  try {
    sessionStorage.removeItem(POST_AUTH_LANDING_HASH_KEY);
    sessionStorage.removeItem(POST_AUTH_FALLBACK_ROUTE_KEY);
    sessionStorage.removeItem(POST_AUTH_GRACE_ACTIVE_KEY);
  } catch (_e) {
    // Ignore: see clearDeepLinkHistoryPair.
  }
}

/**
 * One-shot cleanup that wipes BOTH the deep-link history pair and the
 * post-auth grace triple.
 *
 * Designed to be the single thing logout has to remember to call so that no
 * cross-session leakage can happen (a saved deep link or armed grace window
 * from a previous session must never affect the next user on the same tab).
 *
 * Used by:
 *   - `userActions.logout()` (src/actions/user.actions.js)
 */
export function clearAuthRelatedSessionStorage() {
  clearDeepLinkHistoryPair();
  clearPostAuthGrace();
}

// -----------------------------------------------------------------------------
// Arm / consume / clear-on-nav
// -----------------------------------------------------------------------------

/**
 * Arms the post-auth grace window.
 *
 * Call this ONLY immediately before (or as part of) a redirect to `landingHash`
 * triggered by the auth flow. The grace window is then "live" and one matching
 * 401 from the landing route will be absorbed into a redirect to `fallbackRoute`
 * instead of being treated as session loss.
 *
 * Both inputs are normalized to `#`-prefixed form before storage. If either
 * normalizes to "", the function is a no-op — there is no sensible way to
 * recover from a missing landing or fallback, and arming partial state would
 * mean a future consume call could return null `landing` and silently misbehave.
 *
 * Wrapped in try/catch because session-storage writes can throw on quota or
 * security errors; callers must not depend on persistence being durable.
 *
 * @param {string} landingHash   Where the auth redirect took the user
 *                               (e.g. `#/orders/sales?CompanyRefNumber=GATES10`).
 * @param {string} fallbackRoute Safe route to bounce to on first 401
 *                               (`#/dashboard` or `#/holding`).
 */
export function armPostAuthGrace(landingHash, fallbackRoute) {
  try {
    const landing = normalizeHashForGrace(landingHash);
    const fallback = normalizeHashForGrace(fallbackRoute);
    // Bail out silently rather than persist a half-armed window.
    if (!landing || !fallback) return;
    sessionStorage.setItem(POST_AUTH_LANDING_HASH_KEY, landing);
    sessionStorage.setItem(POST_AUTH_FALLBACK_ROUTE_KEY, fallback);
    sessionStorage.setItem(POST_AUTH_GRACE_ACTIVE_KEY, "1");
  } catch (_e) {
    // Ignore: see storage notes at the top of the file.
  }
}

/**
 * Clears the grace window if the user has navigated away from the landing hash.
 *
 * Wired into `app.js` (the same hashchange Sentry-breadcrumb path that handles
 * MSAL auth handoffs). The intent is "you arrived somewhere new under your own
 * steam, so the deep-link 401 absorber is no longer relevant — future 401s
 * should follow the normal logout path".
 *
 * Behavior:
 *   - No-op if the grace window is not armed.
 *   - No-op if either the stored landing or the new hash normalizes to ""
 *     (we cannot decide hash equality with missing data).
 *   - Otherwise, if `cur !== landing`, clears the grace triple.
 *
 * NOTE: we deliberately do NOT clear when `cur === landing` (e.g. a same-page
 * hash update for query-param tweaks); the grace window remains armed until
 * either consumed or the user moves to a genuinely different route.
 *
 * @param {string} newHash The hash the app is currently transitioning to.
 */
export function maybeClearPostAuthGraceOnHashChange(newHash) {
  try {
    // Cheap early bail to avoid touching landing/fallback when not armed.
    if (sessionStorage.getItem(POST_AUTH_GRACE_ACTIVE_KEY) !== "1") {
      return;
    }
    const landing = normalizeHashForGrace(
      sessionStorage.getItem(POST_AUTH_LANDING_HASH_KEY),
    );
    const cur = normalizeHashForGrace(newHash);
    // Only disarm when we can confidently confirm a change of route.
    // Either side being "" means "I cannot tell" — leave the window alone.
    if (cur && landing && cur !== landing) {
      clearPostAuthGrace();
    }
  } catch (_e) {
    // Ignore: see storage notes at the top of the file.
  }
}

/**
 * Detects a stale deep-link history entry left behind by a different MSAL
 * account.
 *
 * Returns true ONLY when ALL of the following are true:
 *   1. There is a saved `history` hash, AND
 *   2. The current MSAL account has a `homeAccountId`, AND
 *   3. A previous `homeAccountId` was stored alongside the history, AND
 *   4. The two ids differ.
 *
 * In any "I cannot tell" case (no saved history, no current account, no
 * stored id, storage error) the function returns false — better to attempt
 * a possibly-noop deep-link restore than to drop a legitimate one.
 *
 * Used by `auth-finalize.js` to decide whether to wipe the saved history
 * before reading it back: the wipe prevents one user's saved deep link from
 * leaking into another user's session on the same tab.
 *
 * @param {{homeAccountId?: string} | null | undefined} msalAccount
 *   The account MSAL just authenticated (active or freshly returned).
 * @returns {boolean} true if the saved history belongs to a different account.
 */
export function isDeepLinkHistoryStaleForMsalAccount(msalAccount) {
  try {
    const historyHash = sessionStorage.getItem(DEEP_LINK_HISTORY_HASH_KEY);
    if (!historyHash || !msalAccount?.homeAccountId) {
      return false;
    }
    const storedId =
      sessionStorage.getItem(DEEP_LINK_HISTORY_MSAL_HOME_ACCOUNT_KEY) ||
      "";
    // If we never stored an id (legacy flow) we cannot conclude staleness.
    return Boolean(storedId && storedId !== msalAccount.homeAccountId);
  } catch (_e) {
    // On any failure, default to "not stale" — we'd rather honor the deep link.
    return false;
  }
}

// -----------------------------------------------------------------------------
// Consume (the 401 absorber)
// -----------------------------------------------------------------------------

/**
 * Internal helper: returns the fallback route IF and ONLY IF a 401 happening
 * right now should be absorbed by the grace window.
 *
 * All four conditions must be satisfied:
 *   (a) `sessionStorage` is available (skipped during SSR / odd test envs).
 *   (b) The grace window is armed (`POST_AUTH_GRACE_ACTIVE_KEY === "1"`).
 *   (c) Both stored landing and stored fallback normalize to non-empty hashes.
 *   (d) The user is still on the landing hash (`cur === landing`) AND the
 *       fallback is a different route (`cur !== fallback`).
 *
 * Condition (d) has two purposes:
 *   - `cur === landing` means "the 401 is coming from the page we redirected
 *     to". A 401 from a different route is unrelated to this grace window
 *     and must follow the normal logout path.
 *   - `cur !== fallback` prevents a redirect loop in the holding-page case
 *     where landing and fallback are both `#/holding` (eligible-but-no-perms
 *     users): a 401 on `#/holding` should not bounce to `#/holding`.
 *
 * Returns null in any "ineligible" case so the caller can fall through to
 * its normal logout logic without further branching.
 *
 * @param {string} currentHash The user's current `window.location.hash`.
 * @returns {string | null} Fallback route to redirect to, or null.
 */
function getPostAuth401FallbackRouteIfEligible(currentHash) {
  try {
    if (typeof sessionStorage === "undefined") return null;
    if (sessionStorage.getItem(POST_AUTH_GRACE_ACTIVE_KEY) !== "1") {
      return null;
    }
    const landing = normalizeHashForGrace(
      sessionStorage.getItem(POST_AUTH_LANDING_HASH_KEY),
    );
    const fallback = normalizeHashForGrace(
      sessionStorage.getItem(POST_AUTH_FALLBACK_ROUTE_KEY),
    );
    const cur = normalizeHashForGrace(currentHash);
    // Defensive: refuse to act on partial state.
    if (!landing || !fallback) return null;
    // Condition (d) — see header above.
    if (cur !== landing) return null;
    if (cur === fallback) return null;
    return fallback;
  } catch (_e) {
    return null;
  }
}

/**
 * Public consume: ONE-SHOT helper used by the axios 401 interceptor.
 *
 * If the grace window is currently eligible (see
 * `getPostAuth401FallbackRouteIfEligible` for the four conditions), this
 * function:
 *   1. Returns the fallback route the caller should redirect to, AND
 *   2. Clears the grace triple so a subsequent 401 cannot be absorbed.
 *
 * Otherwise returns null and leaves storage untouched, signalling the
 * caller to follow its normal logout path.
 *
 * Caller responsibilities (in `src/helpers/client.js`):
 *   - Show a user-facing warning toast.
 *   - Set `window.location.hash` to the returned route.
 *   - Reject the original promise so callers don't proceed as if the API
 *     call succeeded.
 *
 * Why one-shot:
 *   The grace window is intentionally narrow — it only buys the user one
 *   "you don't have permission for that page" forgiveness. If a second 401
 *   comes in (e.g. dashboard itself fails), we want the standard logout
 *   path to run so the user is not silently trapped in a redirect loop.
 *
 * @param {string} currentHash The user's current `window.location.hash`.
 * @returns {string | null} The fallback route, or null if not absorbing.
 */
export function consumePostAuth401Fallback(currentHash) {
  const route = getPostAuth401FallbackRouteIfEligible(currentHash);
  if (route) {
    // One-shot: disarm before the caller redirects. A subsequent 401
    // (e.g. on the fallback route itself) must follow normal logout flow.
    clearPostAuthGrace();
  }
  return route;
}
